import { ReactNode, Ref, useId, useState } from "react";

import { buttonVariants } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

interface DisclosureProps {
  /** The trigger's text: names what opens, e.g. "Setup instructions". */
  summary: ReactNode;
  children: ReactNode;
  /** Controlled open state; pair it with `onOpenChange`. Leave it out to let the disclosure own it. */
  open?: boolean;
  /** Initial state when uncontrolled. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Keeps the content mounted (and hidden) while closed, so a form inside keeps what was typed.
   * Without it the content unmounts on close.
   */
  keepMounted?: boolean;
  /** The content element's id; generated when left out. */
  contentId?: string;
  /** The trigger button, e.g. to return focus to it after the content closes itself. */
  triggerRef?: Ref<HTMLButtonElement>;
  className?: string;
  contentClassName?: string;
}

/**
 * Shows less-used detail one click away: a ghost button with a chevron that turns when open,
 * announced through `aria-expanded` and `aria-controls`. The trigger sits 12px to the left, so its
 * label lines up with the text around it while the hover fill keeps its padding.
 */
export const Disclosure = ({
  summary,
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  keepMounted = false,
  contentId,
  triggerRef,
  className,
  contentClassName,
}: DisclosureProps) => {
  const generatedId = useId();
  const id = contentId ?? `disclosure-${generatedId}`;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const contentMounted = open || keepMounted;

  const toggle = () => {
    if (controlledOpen === undefined) setUncontrolledOpen(!open);
    onOpenChange?.(!open);
  };

  return (
    <div className={className}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        // Points only at content that is in the document
        aria-controls={contentMounted ? id : undefined}
        onClick={toggle}
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "-ml-3")}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          className={cn(
            "size-4 transition-transform motion-reduce:transition-none",
            open && "rotate-90",
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 4l4 4-4 4" />
        </svg>
        {summary}
      </button>
      {contentMounted && (
        <div id={id} hidden={!open} className={cn("mt-3", contentClassName)}>
          {children}
        </div>
      )}
    </div>
  );
};
